/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ref as databaseRef, set } from 'firebase/database';
import { dbRealtime, storage } from '../environments/firebase';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { Bold, Italic, Underline, Image as ImageIcon, Palette, Text } from "lucide-react";

interface TimeEntry {
  time: string;
  content: string;
  textStyle?: {
    bold?: boolean;
    italic?: boolean;
    underline?: boolean;
    color?: string;
    size?: string;
  };
}

interface DateEntries {
  [date: string]: TimeEntry[];
}

const TEXT_SIZES = [
  { label: 'Small', value: 'small', class: '0.875rem' },
  { label: 'Normal', value: 'normal', class: '1rem' },
  { label: 'Large', value: 'large', class: '1.125rem' },
  { label: 'Extra Large', value: 'xl', class: '1.25rem' }
];

export default function Pravachan() {
  const [vakta, setVakta] = useState('');
  const [showCustomVakta, setShowCustomVakta] = useState(false);
  const [topic, setTopic] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentDate, setCurrentDate] = useState('');
  const [dateEntries, setDateEntries] = useState<DateEntries>({});
  const [currentTime, setCurrentTime] = useState('');
  const [customTime, setCustomTime] = useState('');
  const [useCustomTime, setUseCustomTime] = useState(false);
  const [currentContent, setCurrentContent] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [textStyle, setTextStyle] = useState({
    bold: false,
    italic: false,
    underline: false,
    color: '#000000',
    size: 'normal'
  });
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [showSizePicker, setShowSizePicker] = useState(false);
  const [selection, setSelection] = useState<Range | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const { currentUser } = useAuth();
  
  const contentEditableRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const TIME_SLOTS = Array.from({ length: 24 }, (_, i) => {
    const hour = i % 12 || 12;
    const ampm = i < 12 ? 'AM' : 'PM';
    return `${hour}:00 ${ampm}`;
  });

  const COLORS = ['#3b82f6', '#ef4444', '#10b981', '#f59e0b', '#8b5cf6', '#000000'];
  const VAKTA_OPTIONS = [
    'P.UttamYogi Swami',
    'P.KrishnaPrem Swami',
    'Custom...'
  ];

  useEffect(() => {
    const today = new Date().toISOString().slice(0, 10);
    setStartDate(today);
    setEndDate(today);
    setCurrentDate(today);
    setCurrentTime(TIME_SLOTS[0]);
  }, []);

  const handleVaktaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    if (selectedValue === 'Custom...') {
      setShowCustomVakta(true);
      setVakta('');
    } else {
      setShowCustomVakta(false);
      setVakta(selectedValue);
    }
  };

  const saveSelection = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount > 0) {
      setSelection(sel.getRangeAt(0));
    }
  };

  const restoreSelection = () => {
    if (selection && contentEditableRef.current) {
      const sel = window.getSelection();
      if (sel) {
        sel.removeAllRanges();
        sel.addRange(selection);
      }
    }
  };

  const applyStyleToSelection = (style: string, value?: string) => {
    saveSelection();
    
    if (contentEditableRef.current) {
      const sel = window.getSelection();
      if (sel && !sel.isCollapsed) {
        restoreSelection();
        
        if (style === 'bold') {
          document.execCommand('bold', false);
          setTextStyle(prev => ({ ...prev, bold: !prev.bold }));
        } else if (style === 'italic') {
          document.execCommand('italic', false);
          setTextStyle(prev => ({ ...prev, italic: !prev.italic }));
        } else if (style === 'underline') {
          document.execCommand('underline', false);
          setTextStyle(prev => ({ ...prev, underline: !prev.underline }));
        } else if (style === 'foreColor' && value) {
          document.execCommand('styleWithCSS', false, 'true');
          document.execCommand('foreColor', false, value);
          setTextStyle(prev => ({ ...prev, color: value }));
        } else if (style === 'fontSize' && value) {
          document.execCommand('fontSize', false, '7');
          const fontElements = contentEditableRef.current.getElementsByTagName('font');
          for (let i = 0; i < fontElements.length; i++) {
            const fontElement = fontElements[i] as HTMLFontElement;
            if (fontElement.size === '7') {
              fontElement.outerHTML = `<span style="font-size: ${value}">${fontElement.innerHTML}</span>`;
            }
          }
          setTextStyle(prev => ({ ...prev, size: value }));
        }
      }
    }
  };

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!e.target.files || e.target.files.length === 0) return;
    
    setIsUploading(true);
    const files = Array.from(e.target.files);

    try {
      for (const file of files) {
        const fileRef = storageRef(storage, `pravachan-images/${currentUser?.uid}/${Date.now()}-${file.name}`);
        const snapshot = await uploadBytes(fileRef, file);
        const downloadURL = await getDownloadURL(snapshot.ref);
        
        if (contentEditableRef.current) {
          const img = document.createElement('img');
          img.src = downloadURL;
          img.className = 'max-w-full h-auto max-h-40 rounded border border-gray-200 my-2';
          contentEditableRef.current.appendChild(img);
          setCurrentContent(contentEditableRef.current.innerHTML);
        }
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images. Please try again.');
    } finally {
      setIsUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleCustomTimeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCustomTime(e.target.value);
  };

  const handleBlur = () => {
    if (customTime && !TIME_SLOTS.includes(customTime)) {
      setCurrentTime(customTime);
    }
  };

  const addTimeEntry = () => {
    const timeToUse = useCustomTime && customTime ? customTime : currentTime;
    if (!timeToUse || !currentContent.trim()) return;
    
    const newEntry: TimeEntry = {
      time: timeToUse,
      content: currentContent.trim(),
      textStyle: {
        bold: textStyle.bold,
        italic: textStyle.italic,
        underline: textStyle.underline,
        color: textStyle.color,
        size: textStyle.size
      }
    };
    
    setDateEntries(prev => ({
      ...prev,
      [currentDate]: [...(prev[currentDate] || []), newEntry]
    }));
    
    setCurrentContent('');
    setCustomTime('');
    setUseCustomTime(false);
    setTextStyle({
      bold: false,
      italic: false,
      underline: false,
      color: '#000000',
      size: 'normal'
    });
    
    if (contentEditableRef.current) {
      contentEditableRef.current.innerHTML = '';
    }
    
    if (!useCustomTime) {
      const currentIndex = TIME_SLOTS.indexOf(currentTime);
      if (currentIndex < TIME_SLOTS.length - 1) {
        setCurrentTime(TIME_SLOTS[currentIndex + 1]);
      }
    }
  };

  const removeTimeEntry = (date: string, index: number) => {
    setDateEntries(prev => {
      const newEntries = { ...prev };
      newEntries[date] = newEntries[date].filter((_, i) => i !== index);
      if (newEntries[date].length === 0) {
        delete newEntries[date];
      }
      return newEntries;
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !Object.keys(dateEntries).length) return;
    
    setIsSubmitting(true);

    try {
      const savePromises = Object.entries(dateEntries).map(async ([date, entries]) => {
        if (!entries.length) return;
        
        const timestamp = new Date(date).getTime();
        const pravachanRef = databaseRef(dbRealtime, `users/${currentUser.uid}/BrahmnaSange/${timestamp}`);
        
        const data = {
          vakta: vakta.trim(),
          topic: topic.trim(),
          dateRange: {
            start: startDate,
            end: endDate
          },
          currentDate: date,
          timeEntries: entries,
          createdAt: { '.sv': 'timestamp' }
        };

        await set(pravachanRef, data);
      });

      await Promise.all(savePromises);

      setDateEntries({});
      setCurrentContent('');
      setCurrentDate(startDate);
      setCurrentTime(TIME_SLOTS[0]);
      setCustomTime('');
      setUseCustomTime(false);
      setTextStyle({
        bold: false,
        italic: false,
        underline: false,
        color: '#000000',
        size: 'normal'
      });
      
      if (contentEditableRef.current) {
        contentEditableRef.current.innerHTML = '';
      }
      
      alert('Pravachan saved successfully!');
    } catch (error) {
      console.error('Error saving data:', error);
      alert('Failed to save pravachan. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAvailableTimes = () => {
    const usedTimes = (dateEntries[currentDate] || []).map(entry => entry.time);
    return TIME_SLOTS.filter(time => !usedTimes.includes(time));
  };

  const renderTextWithStyles = (content: string) => {
    return <div dangerouslySetInnerHTML={{ __html: content }} />;
  };

  const generateDateOptions = () => {
    if (!startDate || !endDate) return [];
    
    const dates = [];
    const start = new Date(startDate);
    const end = new Date(endDate);
    
    for (let d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      const dateStr = d.toISOString().slice(0, 10);
      dates.push(dateStr);
    }
    
    return dates;
  };

  const formatDateDisplay = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      weekday: 'short', 
      month: 'short', 
      day: 'numeric', 
      year: 'numeric' 
    });
  };

  const formatTimeDisplay = (timeStr: string) => {
    if (TIME_SLOTS.includes(timeStr)) return timeStr;
    
    const timeMatch = timeStr.match(/(\d+):(\d+)\s*(AM|PM)?/i);
    if (timeMatch) {
      let hours = parseInt(timeMatch[1]);
      const minutes = timeMatch[2];
      const period = timeMatch[3]?.toUpperCase() || (hours >= 12 ? 'PM' : 'AM');
      
      if (period === 'PM' && hours < 12) hours += 12;
      if (period === 'AM' && hours === 12) hours = 0;
      
      return `${hours % 12 || 12}:${minutes} ${period}`;
    }
    
    return timeStr;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md overflow-hidden">
        <form onSubmit={handleSubmit} className="flex flex-col h-full">
          <div className="p-6 space-y-6 flex-1 overflow-auto">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Vakta (Speaker):</label>
                {showCustomVakta ? (
                  <input
                    type="text"
                    value={vakta}
                    onChange={(e) => setVakta(e.target.value)}
                    placeholder="Enter speaker's name"
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                  />
                ) : (
                  <select
                    value={vakta}
                    onChange={handleVaktaChange}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    required
                  >
                    <option value="">Select a speaker</option>
                    {VAKTA_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        {option}
                      </option>
                    ))}
                  </select>
                )}
              </div>

              <div>
                <label className="block text-gray-700 font-medium mb-2">Topic:</label>
                <input
                  type="text"
                  value={topic}
                  onChange={(e) => setTopic(e.target.value)}
                  placeholder="Enter pravachan topic"
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-gray-700 font-medium mb-2">Report Start Date:</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => {
                    const newStart = e.target.value;
                    setStartDate(newStart);
                    if (new Date(newStart) > new Date(currentDate)) {
                      setCurrentDate(newStart);
                    }
                    if (new Date(newStart) > new Date(endDate)) {
                      setEndDate(newStart);
                    }
                  }}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">Report End Date:</label>
                <input
                  type="date"
                  value={endDate}
                  min={startDate}
                  onChange={(e) => {
                    const newEnd = e.target.value;
                    setEndDate(newEnd);
                    if (new Date(newEnd) < new Date(currentDate)) {
                      setCurrentDate(newEnd);
                    }
                  }}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                />
              </div>
              
              <div>
                <label className="block text-gray-700 font-medium mb-2">Report Date:</label>
                <select
                  value={currentDate}
                  onChange={(e) => {
                    setCurrentDate(e.target.value);
                    setCurrentTime(TIME_SLOTS[0]);
                    setCustomTime('');
                    setUseCustomTime(false);
                  }}
                  className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                  required
                >
                  {generateDateOptions().map((dateStr) => (
                    <option key={dateStr} value={dateStr}>
                      {formatDateDisplay(dateStr)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div className="flex justify-between items-center mb-2">
              <label className="block text-gray-700 font-medium">Add Entry:</label>
              <div className="flex space-x-2">
                <div className="flex space-x-1">
                  <button
                    type="button"
                    onClick={() => {
                      saveSelection();
                      applyStyleToSelection('bold');
                    }}
                    className={`p-2 rounded-lg ${textStyle.bold ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'} hover:bg-gray-200 transition`}
                    title="Bold"
                  >
                    <Bold size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      saveSelection();
                      applyStyleToSelection('italic');
                    }}
                    className={`p-2 rounded-lg ${textStyle.italic ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'} hover:bg-gray-200 transition`}
                    title="Italic"
                  >
                    <Italic size={18} />
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      saveSelection();
                      applyStyleToSelection('underline');
                    }}
                    className={`p-2 rounded-lg ${textStyle.underline ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'} hover:bg-gray-200 transition`}
                    title="Underline"
                  >
                    <Underline size={18} />
                  </button>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        saveSelection();
                        setShowColorPicker(!showColorPicker);
                      }}
                      className={`p-2 rounded-lg ${showColorPicker ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'} hover:bg-gray-200 transition`}
                      title="Text Color"
                    >
                      <Palette size={18} />
                    </button>
                    {showColorPicker && (
                      <div className="absolute right-0 mt-2 w-32 p-2 bg-white rounded-lg shadow-lg z-50 grid grid-cols-3 gap-2">
                        {COLORS.map(color => (
                          <button
                            key={color}
                            type="button"
                            className={`w-6 h-6 rounded-full hover:ring-2 hover:ring-blue-300`}
                            style={{ backgroundColor: color }}
                            onClick={() => {
                              applyStyleToSelection('foreColor', color);
                              setShowColorPicker(false);
                            }}
                            title={color}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="relative">
                    <button
                      type="button"
                      onClick={() => {
                        saveSelection();
                        setShowSizePicker(!showSizePicker);
                      }}
                      className={`p-2 rounded-lg ${showSizePicker ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'} hover:bg-gray-200 transition`}
                      title="Text Size"
                    >
                      <Text size={18} />
                    </button>
                    {showSizePicker && (
                      <div className="absolute right-0 mt-2 w-32 p-2 bg-white rounded-lg shadow-lg z-50 space-y-1">
                        {TEXT_SIZES.map(size => (
                          <button
                            key={size.value}
                            type="button"
                            className={`w-full text-left px-2 py-1 rounded hover:bg-gray-100 ${textStyle.size === size.value ? 'bg-blue-50 text-blue-600' : ''}`}
                            onClick={() => {
                              applyStyleToSelection('fontSize', size.class);
                              setTextStyle(prev => ({ ...prev, size: size.value }));
                              setShowSizePicker(false);
                            }}
                          >
                            {size.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className={`p-2 rounded-lg ${isUploading ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'} hover:bg-gray-200 transition`}
                    title="Insert Image"
                    disabled={isUploading}
                  >
                    <ImageIcon size={18} />
                    <input
                      type="file"
                      ref={fileInputRef}
                      onChange={handleImageUpload}
                      accept="image/*"
                      multiple
                      className="hidden"
                    />
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
              <div className="md:col-span-1">
                <label className="block text-gray-700 font-medium mb-2">Time:</label>
                <div className="space-y-2">
                  <select
                    value={currentTime}
                    onChange={(e) => {
                      setCurrentTime(e.target.value);
                      setUseCustomTime(false);
                    }}
                    className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    disabled={useCustomTime}
                  >
                    {getAvailableTimes().map(time => (
                      <option key={time} value={time}>{time}</option>
                    ))}
                  </select>
                  <div className="flex items-center">
                    <input
                      type="checkbox"
                      id="customTimeCheckbox"
                      checked={useCustomTime}
                      onChange={(e) => setUseCustomTime(e.target.checked)}
                      className="mr-2"
                    />
                    <label htmlFor="customTimeCheckbox" className="text-sm text-gray-600">Custom Time</label>
                  </div>
                  {useCustomTime && (
                    <input
                      type="text"
                      value={customTime}
                      onChange={handleCustomTimeChange}
                      onBlur={handleBlur}
                      placeholder="e.g. 4:12 AM"
                      className="w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400"
                    />
                  )}
                </div>
              </div>
              <div className="md:col-span-3">
                <label className="block text-gray-700 font-medium mb-2">Content:</label>
                <div className="flex items-end gap-2">
                  <div
                    ref={contentEditableRef}
                    contentEditable
                    onInput={(e) => setCurrentContent(e.currentTarget.innerHTML)}
                    onBlur={saveSelection}
                    onFocus={restoreSelection}
                    className="relative flex-1 w-full px-4 py-2 rounded-lg border border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-400 min-h-[60px] bg-white overflow-auto"
                  >
                    {currentContent === '' && (
                      <span className="absolute text-gray-400 pointer-events-none">
                        • Describe what happened at this time...
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={addTimeEntry}
                    disabled={!currentTime || !currentContent.trim()}
                    className="h-[42px] px-4 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition disabled:opacity-50"
                  >
                    Add
                  </button>
                </div>
              </div>
            </div>

            {Object.keys(dateEntries).length > 0 && (
              <div className="mt-8">
                <h3 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b border-gray-200">
                  {formatDateDisplay(currentDate)}
                </h3>
                <div className="space-y-4">
                  {(dateEntries[currentDate] || []).map((entry, index) => (
                    <div key={index} className="bg-gray-50 p-4 rounded-lg shadow-sm">
                      <div className="flex justify-between items-start mb-2">
                        <span className="font-medium text-blue-600">{formatTimeDisplay(entry.time)}</span>
                        <button
                          type="button"
                          onClick={() => removeTimeEntry(currentDate, index)}
                          className="text-red-500 hover:text-red-700 transition"
                          title="Remove entry"
                        >
                          ×
                        </button>
                      </div>
                      <div className="prose max-w-none">
                        {renderTextWithStyles(entry.content)}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="p-4 border-t border-gray-200 bg-white">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-500">
                {Object.values(dateEntries).reduce((total, entries) => total + entries.length, 0) > 0 && (
                  <span>
                    {Object.values(dateEntries).reduce((total, entries) => total + entries.length, 0)} 
                    entries across {Object.keys(dateEntries).length} dates
                  </span>
                )}
              </div>
              <button
                type="submit"
                disabled={isSubmitting || Object.values(dateEntries).reduce((total, entries) => total + entries.length, 0) === 0}
                className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-lg transition shadow-md disabled:opacity-70"
              >
                {isSubmitting ? 'Saving...' : 'Save Pravachan'}
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}